// common actions or common util fns used across the app
import queryString from 'query-string';
import renderHTML from 'html-react-parser';
import crypto from 'crypto-js';
import { isIOS } from 'react-device-detect';
import { saveAs } from 'file-saver/FileSaver';
import jwtDecode from 'jwt-decode';
import history from './app-history';
import {
  APP_CONFIG,
  API_GENERAL_ERROR,
  DEBUG_MODE,
  ROUTE_PATHS,
  PRODUCT_SELECTION_DATA,
  ENABLE_LIVE_CHAT,
  ENABLE_NEW_HEADER_FOOTER_DESIGN,
  LIVE_CHAT_SUPPORTED_PRODUCTS,
  SECRET_KEY,
  COMPANY_NAME_PREFIX,
  SESSION_RENEWAL_START,
  SESSION_RENEWAL_COMPLETE,
  SESSION_RENEWAL_POPUP_CLOSE,
  SESSION_RENEWAL_FAILED,
} from './app-constants';
import { omnitureTriggerEventAction } from '../src/utilities/omniture-data-layer';

export const shouldRedirectToError = () => {
  const isTestEnviroment = process.env.NODE_ENV === 'test';
  if (!isTestEnviroment) {
    const isLocationPathNameAlreadyError = window.location.pathname.search('error') > 0;
    if (!isLocationPathNameAlreadyError) {
      history.push(`${APP_CONFIG.reactBasePath}/error`);
    }
  }
};

export const failureAction = (errorInfo) => {
  shouldRedirectToError();
  return { type: API_GENERAL_ERROR, errorInfo };
};

export const redirectToErrorPage = () => {
  shouldRedirectToError();
  return { type: API_GENERAL_ERROR };
};

// if array length is greater than the param length value then trim array
export const trimArray = (inArray = [], maxLength = 0) => (inArray.length > maxLength ? inArray.slice(0, maxLength) : inArray);

export const getQueryStringHashToObject = locationHash => queryString.parse(locationHash);

// array count by match
export const arrayCountByMatch = (inArray = [], matchAttribute, matchValue) => (
  inArray.reduce((count, element) => (count + ((element[matchAttribute] === matchValue) ? 1 : 0)), 0)
);

// render as HTML
export const renderAsHtml = content => (content && content.length > 0 ? renderHTML(content) : '');

export const getJWTDecodedObject = jwtToken => jwtDecode(jwtToken);

export const formatNameWithPipeSeparator = () => {
  let customerName;
  const parsedHash = getQueryStringHashToObject(sessionStorage.getItem('locationHash'));
  if (parsedHash.id_token) {
    const jwtDecoded = getJWTDecodedObject(parsedHash.id_token);
    if (jwtDecoded.given_name && jwtDecoded.family_name) {
      customerName = `${jwtDecoded.given_name} ${jwtDecoded.family_name}`;
    }
  }
  return customerName;
};

export const formatCustomerName = () => {
  let customerName = '';
  if (sessionStorage.getItem('locationHash')) {
    customerName = formatNameWithPipeSeparator();
  }
  return customerName;
};

/* remove decimal values from a number to a specified precision
 e.g- (19.9 + 1.2) = 21.099999999999998 will be converted as 21.1 */
export const roundNumberToDecimals = (inputNumber, decimals = 2) => Number((inputNumber).toFixed(decimals));

export const isRedirectedAfterLogin = (locationHash) => {
  const queryStringObject = getQueryStringHashToObject(locationHash);
  const locationHashPresent = sessionStorage.getItem('locationHash') === null;
  return (queryStringObject.access_token && queryStringObject.id_token) || queryStringObject.cidn || !locationHashPresent;
};

export const getAuthorizationAccessToken = () => {
  const windowLocationHash = window.location.hash.search('access_token') !== -1 ? window.location.hash : '';
  const sessionLocationHash = sessionStorage.getItem('locationHash');
  const locationHashToBeUsed = sessionLocationHash === null ? windowLocationHash : sessionLocationHash;
  const parsedHash = getQueryStringHashToObject(locationHashToBeUsed);
  return `Bearer ${parsedHash.access_token}`;
};

export const getIconForPosition = (position, activeItemPosition) => {
  const numbers = [
    'one',
    'two',
    'three',
    'four',
    'five',
    'six',
    'seven',
    'eight',
    'nine',
    'ten',
  ];

  let iconClass = `icon-number-${numbers[position - 1]}`;

  iconClass += activeItemPosition === position ? '-invert' : '';
  return iconClass;
};

export const clearSessionStorage = (logoutUrl) => {
  omnitureTriggerEventAction('logout link clicked');
  if (sessionStorage.getItem('renewInterval')) {
    const renewalKey = sessionStorage.getItem('renewInterval');
    clearInterval(renewalKey);
  }
  sessionStorage.clear();
  window.location = logoutUrl;
};

export const generateRandomString = (length) => {
  let randomText = '';
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  for (let i = 0; i < length; i += 1) {
    randomText += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return randomText;
};

export const setRedirectToLogoutAutomatically = () => ({ type: '' });

export const getParsedHash = (locationHash) => {
  const locationHashPresent = sessionStorage.getItem('locationHash') === null;
  if (locationHashPresent) {
    return getQueryStringHashToObject(locationHash);
  }
  return getQueryStringHashToObject(sessionStorage.getItem('locationHash'));
};

export const debugLogging = (debugText) => {
  if (DEBUG_MODE) {
    console.log(debugText); // eslint-disable-line
  }
};

const removeRenewalFrame = () => {
  debugLogging('start removal of iframe');
  const renewFrameNode = document.getElementById('renewTokenFrame');
  if (renewFrameNode) {
    debugLogging('iframe present');
    window.document.body.removeChild(renewFrameNode);
    debugLogging('complete removal of iframe');
  }
};

export const createRenewFrame = (dispatch, frameUrl, timeInMilliSeconds) => {
  const renewFrame = window.document.createElement('iframe');
  renewFrame.id = 'renewTokenFrame';
  renewFrame.style.visibility = 'hidden';
  renewFrame.style.position = 'absolute';
  renewFrame.style.display = 'none';
  renewFrame.style.width = 0;
  renewFrame.style.height = 0;
  renewFrame.src = frameUrl;
  // renewFrame.src = 'http://localhost:3000/business-pop-mail#userId=farrukh&access_token=jjjjj';
  renewFrame.onload = () => {
    const frameWindow = renewFrame.contentWindow;
    if (DEBUG_MODE) {
      console.log(frameWindow.location);  // eslint-disable-line
    }
    sessionStorage.setItem('locationHash', frameWindow.location.hash);
    const newLocationHash = frameWindow.location.hash;
    removeRenewalFrame();
    if (newLocationHash.includes('access_token')) {
      dispatch({ type: SESSION_RENEWAL_COMPLETE });
    } else {
      dispatch({ type: SESSION_RENEWAL_FAILED });
    }
  };
  const renewInterval = setInterval(() => {
    dispatch({ type: SESSION_RENEWAL_START });
    removeRenewalFrame();
    window.document.body.appendChild(renewFrame);
  }, timeInMilliSeconds);
  sessionStorage.setItem('renewInterval', renewInterval);
  window.tcom.researchInsights.appConfig.renewInterval = renewInterval;
};

export const setupSilentRenew = (cipLoginUrl, locationHash, timeInMinutes) => (dispatch) => {
  const parsedHash = getParsedHash(locationHash);

  const timeInMilliSeconds = timeInMinutes * 60 * 1000;
  const frameUrl = `${cipLoginUrl}&prompt=none&id_token_hint=${parsedHash.id_token}`;

  createRenewFrame(dispatch, frameUrl, timeInMilliSeconds);

  return { type: '' };
};

export const closeSessionPopRenewalPopup = () => (dispatch) => {
  dispatch({ type: SESSION_RENEWAL_POPUP_CLOSE });
};

export const formatNumberToCurrency = (numberToBeFormatted, currenySign) => {
  const formattedNumber = Number(numberToBeFormatted).toFixed().replace(/(\d)(?=(\d{3})+(,|$))/g, '$1,');
  return currenySign + formattedNumber;
};

export const getEventRouteAndUserHashDetails = () => {
  const pathName = window.location.pathname;
  const hash = window.location.hash;
  const route = pathName.replace(APP_CONFIG.reactBasePath, '');
  console.log(route);

  const partnerViewRoutePresent = route.search(ROUTE_PATHS.partnerView.route) !== -1;
  const eventRoutePresent = route.search(ROUTE_PATHS.vantage.route) !== -1;
  const hostedMigrationRoutePresent = route.search(ROUTE_PATHS.hostedMigration.route) !== -1;
  const businessPopMailRoutePresent = route.search(ROUTE_PATHS.businessPopMail.route) !== -1;
  const adaptiveMobilityExternalPresent = route.search(ROUTE_PATHS.adaptiveMobilityExternal.route) !== -1;
  const parsedHash = hash.length > 2 ? getQueryStringHashToObject(hash) : null;
  return {
    isEventRouteAndUserHashPresent: eventRoutePresent && partnerViewRoutePresent === false,
    isHostedRoutePresent: hostedMigrationRoutePresent,
    isBusinessPopMailRoutePresent: businessPopMailRoutePresent,
    isAdaptiveMobilityExternalPresent: adaptiveMobilityExternalPresent,
    useridHashValue: parsedHash ? parsedHash.userId : '',
    isPartnerView: partnerViewRoutePresent,
  };
};

export const downloadFileBlob = (response, filename, mimeType) => {
  if (isIOS) {
    const blobData = new Blob([response.data], { type: mimeType });
    saveAs(blobData, filename);
  } else if (window.navigator && window.navigator.msSaveOrOpenBlob) { // for IE and Edge
    const blobData = new Blob([response.data], { type: mimeType });
    window.navigator.msSaveOrOpenBlob(blobData, filename);
  } else {
    const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      // For Firefox it is necessary to delay revoking the ObjectURL
      window.URL.revokeObjectURL(url);
    }, 100);
  }
};

export const downloadFileFromLink = (url, iosHiddenButtonId) => {
  const linkButton = document.getElementById(iosHiddenButtonId);
  linkButton.setAttribute('href', url);
  linkButton.click();
};

export const getProductNameFromProductKey = (key) => {
  const productsData = PRODUCT_SELECTION_DATA.split(',');
  const product = productsData.find(data => data.includes(key));
  if (product) {
    return product.split('|')[1];
  }
  return 'No Match';
};

export const getProductNameText = (product) => {
  const productName = getProductNameFromProductKey(product);
  return productName;
};

export const getCurrentProduct = () => {
  let currentProduct = '';
  const routeDetails = getEventRouteAndUserHashDetails();
  currentProduct = sessionStorage.getItem('currentProduct');
  if (routeDetails.isPartnerView) {
    currentProduct = 'AMP';
  } else if (currentProduct === null || currentProduct === undefined) {
    currentProduct = window.parent.tcom.researchInsights.appConfig.currentProduct;
  }
  return currentProduct;
};


export const setLiveChat = (enable) => {
  const currentProduct = getCurrentProduct();
  const liveChatButton = document.querySelector('.embeddedServiceHelpButton .helpButton .uiButton.helpButtonEnabled');
  if (ENABLE_LIVE_CHAT) {
    if (enable && window.initSFDCChat) {
      if (DEBUG_MODE) {
        console.log('live chat process started for product:', currentProduct);    // eslint-disable-line
      }
      if (liveChatButton && liveChatButton.hidden) {
        liveChatButton.hidden = false;
      } else {
        window.initSFDCChat();
      }
    } else if (!enable) {
      if (liveChatButton && liveChatButton.hidden === false) {
        liveChatButton.hidden = true;
      }
    }
  }
};

export const showHideOldNewHeaderFooter = (showNewHeader) => {
  if (window === window.parent && ENABLE_NEW_HEADER_FOOTER_DESIGN) {
    if (showNewHeader) {
      const localHeader = document.getElementsByClassName('localHeader')[0];
      if (localHeader) {
        localHeader.classList.add('d-none');
      }
      const localFooter = document.getElementsByClassName('localFooter')[0];
      if (localFooter) {
        localFooter.classList.add('d-none');
      }
      const globalHeader = document.getElementsByClassName('globalHeader')[0];
      if (globalHeader) {
        globalHeader.classList.remove('d-none');
      }
      const globalFooter = document.getElementsByClassName('globalFooter')[0];
      if (globalFooter) {
        globalFooter.classList.remove('d-none');
      }
    } else {
      const globalHeader = document.getElementsByClassName('globalHeader')[0];
      if (globalHeader) {
        globalHeader.classList.add('d-none');
      }
      const globalFooter = document.getElementsByClassName('globalFooter')[0];
      if (globalFooter) {
        globalFooter.classList.add('d-none');
      }
      const localHeader = document.getElementsByClassName('localHeader')[0];
      if (localHeader) {
        localHeader.classList.remove('d-none');
      }
      const localFooter = document.getElementsByClassName('localFooter')[0];
      if (localFooter) {
        localFooter.classList.remove('d-none');
      }
    }
  }
};

export const restartLiveChatForProduct = () => {
  const currentProduct = getCurrentProduct();
  const scriptTag = document.getElementById('live-chat-script');
  if (scriptTag) {
    if (DEBUG_MODE) {
      console.log('live chat script tag removal section');    // eslint-disable-line
    }
    document.body.removeChild(scriptTag);
  }
  if (window.initSFDCChat && LIVE_CHAT_SUPPORTED_PRODUCTS.includes(currentProduct)) {
    if (DEBUG_MODE) {
      console.log('live chat process started for product:', currentProduct);    // eslint-disable-line
    }
    window.initSFDCChat(window.liveChatContentData, currentProduct, DEBUG_MODE);
    const liveChatButton = document.querySelector('.embeddedServiceHelpButton .helpButton .uiButton.helpButtonEnabled');
    if (liveChatButton && liveChatButton.hidden) {
      liveChatButton.hidden = false;
    }
  } else {
    const liveChatButton = document.querySelector('.embeddedServiceHelpButton .helpButton .uiButton.helpButtonEnabled');
    if (liveChatButton && liveChatButton.hidden === false) {
      liveChatButton.hidden = true;
    }
  }
};

export const updateLiveChatValues = (liveChatParams, currentProduct) => {
  if (ENABLE_LIVE_CHAT && LIVE_CHAT_SUPPORTED_PRODUCTS.includes(currentProduct)) {
    window.embedded_svc.settings.buttonId = liveChatParams.LCButtonId;
    window.embedded_svc.settings.deploymentId = liveChatParams.LCDeploymentId;
    if (window.embedded_svc.liveAgentAPI) {
      window.embedded_svc.liveAgentAPI.ping();
    }
  }
};

export const closeOffPreviousProductLiveChat = () => {
  const currentProduct = getCurrentProduct();
  if (ENABLE_LIVE_CHAT && LIVE_CHAT_SUPPORTED_PRODUCTS.includes(currentProduct)) {
    const closeChatBtn = document.getElementsByClassName('closeButton');
    if (closeChatBtn && closeChatBtn.length > 0) {
      closeChatBtn[0].click();
    }
    window.embedded_svc.isButtonDisabled = true;
  }
};

export const checkHtmlInPathName = () => {
  const path = window.location.pathname;
  return path.search('.html') !== -1;
};
export const getDecryptedText = (text) => {
  const parsedBase64Key = crypto.enc.Base64.parse(SECRET_KEY);
  const decrptedData = crypto.AES.decrypt(text, parsedBase64Key, { mode: crypto.mode.ECB, padding: crypto.pad.Pkcs7 });
  return decrptedData.toString(crypto.enc.Utf8);
};

export const getCidnValue = (key, dataList, truncateCompanyName) => {
  const selectedItem = dataList.find(item => item.encryptedCidn === key);
  let accountNameToBeUsed = '';
  if (selectedItem && truncateCompanyName) {
    const accountNameTruncated = selectedItem.accountName.length > 10 ?
      `${COMPANY_NAME_PREFIX} ${selectedItem.accountName.substring(0, 11)}...` : selectedItem.accountName;
    accountNameToBeUsed = `${accountNameTruncated} (${getDecryptedText(key)})`;
  } else if (selectedItem) {
    accountNameToBeUsed = `${COMPANY_NAME_PREFIX} ${selectedItem.accountName}`;
  }
  return accountNameToBeUsed;
};


export const showHideElement = (show, elementId) => {
  const element = document.getElementById(elementId);
  if (element) {
    if (show) {
      element.classList.remove('d-none');
    } else {
      element.classList.add('d-none');
    }
  }
};

export const formatNumber = (val, n, x) => {
  const re = '\\d(?=(\\d{' + (x || 3) + '})+' + (n > 0 ? '\\.' : '$') + ')'; // eslint-disable-line
  return val.toFixed(Math.max(0, ~~n)).replace(new RegExp(re, 'g'), '$&,'); // eslint-disable-line
};

export const getEncryptedText = (text) => {
  const parsedBase64Key = crypto.enc.Base64.parse(SECRET_KEY);
  const encryptedData = crypto.AES.encrypt(text, parsedBase64Key, { mode: crypto.mode.ECB, padding: crypto.pad.Pkcs7 });
  return encryptedData.ciphertext.toString(crypto.enc.Base64);
};


export const saveCustomerCidnIfPresent = () => {
  const params = getQueryStringHashToObject(window.location.hash);
  if (params.userId) {
    localStorage.setItem('userId', params.userId);
  }
};

export const getAuthorizationAccessTokenSecureAuth = () => {
  const accessToken = sessionStorage.getItem('accessToken');
  return `Bearer ${accessToken}`;
};

export const getCustomerNameFromSecureAuth = () => {
  let customerName = '';
  const jwtData = sessionStorage.getItem('jwtData');
  if (jwtData) {
    const jwtJson = JSON.parse(jwtData);
    customerName = `${jwtJson.given_name} ${jwtJson.family_name}`;
  }
  return customerName;
};
